import type { AxisProperties } from "./api";
import t from "./translations";

// Ordered most to least severe - shared with LabelEditDialog so a label's
// resulting criticality is derived from the same bands, not redefined.
export const SEVERITY_ROWS = [
  { key: "1", thresholdKey: "threshold_1", label: t.SeverityCriticalLabel },
  { key: "2", thresholdKey: "threshold_2", label: t.SeverityIssueLabel },
  {
    key: "3",
    thresholdKey: "threshold_3",
    label: t.SeverityShouldBeFixedLabel,
  },
  { key: "4", thresholdKey: "threshold_4", label: t.SeverityGoodToKnowLabel },
] as const;

export const NO_ISSUE_KEY = "none";

export type SeverityKey =
  | (typeof SEVERITY_ROWS)[number]["key"]
  | typeof NO_ISSUE_KEY;

// Finds which severity band a value falls into given an axis's thresholds,
// or null if it doesn't reach any of them ("no issue"). Shared with
// LabelEditDialog so both classify values against the same bands.
export function classifySeverity(
  axis: AxisProperties,
  value: number
): (typeof SEVERITY_ROWS)[number] | null {
  return SEVERITY_ROWS.find((row) => value >= axis[row.thresholdKey]) ?? null;
}

// classifySeverity's band label, or the "no issue" fallback.
export function severityLabel(axis: AxisProperties, value: number): string {
  return classifySeverity(axis, value)?.label ?? t.SeverityNoIssueLabel;
}

export function withValueSeverity(
  axis: AxisProperties,
  axisValue: number,
  severityKey: SeverityKey
): AxisProperties {
  const severityIndex =
    severityKey === NO_ISSUE_KEY
      ? SEVERITY_ROWS.length
      : SEVERITY_ROWS.findIndex((row) => row.key === severityKey);

  const next = { ...axis };
  SEVERITY_ROWS.forEach((row, rowIndex) => {
    next[row.thresholdKey] =
      rowIndex < severityIndex
        ? Math.max(next[row.thresholdKey], axisValue + 1)
        : Math.min(next[row.thresholdKey], axisValue);
  });
  return next;
}
